/**
 * Everything the OE widgets read, assembled once per page (Beta-1 plan P3-20 / B4): scores,
 * surveys, response counts, adjustments, conditions, case studies, warnings, scope options.
 * `DriftDashboard` and the access-level dashboards (ITM / FLS / DH / Executive) call this and hand
 * the result to `OEDashboardProvider`, so one data path feeds every dashboard key.
 *
 *   const data = useOEDashboardData({ organization, scope, queryScope, expandedQuadrant, onQuadrantClick });
 *   <OEDashboardProvider value={data.dashboardData}> … </OEDashboardProvider>
 */
import { useEffect, useMemo, useState } from 'react';
import { useOEScores } from '../hooks/useOEScores';
import { useSurveys } from '../hooks/useSurveys';
import { useResponseCounts } from '../hooks/useResponseCounts';
import { useOEAdjustments } from '../hooks/useOEAdjustments';
import { useTRLs } from '../hooks/useTRLs';
import { useOwnedTRLs, mergeConditions } from '../hooks/useOwnedTRLs';
import { useConditionViewer } from '../hooks/useConditionViewer';
import { computeConditionLoad, normalizeScoring } from '../utils/conditionLoad';
import { headcountForScope } from '../utils/rollups';
import { DEFAULT_THRESHOLDS } from '../utils/constants';
import { checkThresholds } from '../utils/scoring';
import { surveyVisibleTo } from '../utils/surveyScope';
import { getVisibleCaseStudies } from '../../../services/documentService';

/** Scopes a widget may pin itself to (P3-18): the viewer's slice under the persona lock. */
export function scopeOptionsFor(organization, queryScope) {
  if (!organization) return [];
  const lockSite = queryScope?.siteId || null;
  const lockDept = queryScope?.deptId || null;
  const lockTeam = queryScope?.nwtId || null;
  const sites = (organization.sites || []).filter((st) => !lockSite || st.id === lockSite);
  const depts = (organization.departments || []).filter((d) => (!lockSite || d.siteId === lockSite) && (!lockDept || d.id === lockDept));
  const deptIds = new Set(depts.map((d) => d.id));
  const teams = (organization.teams || []).filter((t) => deptIds.has(t.departmentId) && (!lockTeam || t.id === lockTeam));
  const out = [];
  if (!lockDept && !lockTeam) sites.forEach((st) => out.push({ value: `site:${st.id}`, label: `Site · ${st.name}` }));
  if (!lockTeam) depts.forEach((d) => out.push({ value: `department:${d.id}`, label: `Department · ${d.name}` }));
  teams.forEach((t) => out.push({ value: `team:${t.id}`, label: `NWT · ${t.name}` }));
  return out;
}

export function filterConditionsForScope(allConditions, scope) {
  if (!scope || scope.type === 'organization') return allConditions;
  if (scope.type === 'site') return allConditions.filter((c) => c.siteId === scope.id);
  if (scope.type === 'department') return allConditions.filter((c) => c.deptId === scope.id || c.ownerDeptId === scope.id);
  if (scope.type === 'team') return allConditions.filter((c) => c.nwtId === scope.id || c.ownerNwtId === scope.id);
  return allConditions;
}

export function useOEDashboardData({ organization, scope, queryScope, expandedQuadrant = null, onQuadrantClick = () => {}, caseStudiesLimit = 3 }) {
  const orgId = organization?.id || null;
  const thresholds = organization?.thresholds || DEFAULT_THRESHOLDS;

  const { snapshots, latestScores: storedScores, warnings: snapshotWarnings, loading: scoresLoading } = useOEScores(orgId, scope);

  const { activeSurveys: allActiveSurveys, surveys, loading: surveysLoading } = useSurveys(orgId);
  // P3-03: survey visibility = target scope ⊆ viewer scope.
  const activeSurveys = useMemo(
    () => allActiveSurveys.filter((sv) => surveyVisibleTo(sv, queryScope, organization)),
    [allActiveSurveys, queryScope, organization]
  );

  const { totalResponses, responseCounts } = useResponseCounts(orgId);

  const { trls: scopedConditions } = useTRLs({ limit: 300 });
  // Tim 20 Oct: what you personally logged or own follows you to every scope (a condition logged
  // for another team must still appear under "Mine"); department-owned items stay scoped.
  const viewer = useConditionViewer();
  const owned = useOwnedTRLs(viewer);
  const personal = useMemo(
    () => (owned || []).filter((c) => (viewer?.uid && (c.submittedBy === viewer.uid || c.ownerUid === viewer.uid))),
    [owned, viewer?.uid]
  );
  const allConditions = useMemo(() => mergeConditions(scopedConditions, owned), [scopedConditions, owned]);
  const conditions = useMemo(() => mergeConditions(filterConditionsForScope(allConditions, scope), personal), [allConditions, scope, personal]);

  const [caseStudies, setCaseStudies] = useState([]);
  const [caseStudiesLoading, setCaseStudiesLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCaseStudiesLoading(true);
      try {
        const data = await getVisibleCaseStudies(orgId, { limit: caseStudiesLimit });
        if (!cancelled) setCaseStudies(data);
      } catch (err) {
        console.error('Failed to fetch case studies for dashboard:', err);
        if (!cancelled) setCaseStudies([]);
      } finally {
        if (!cancelled) setCaseStudiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseStudiesLimit]);

  // Conditions-only scoring (Tim 20 Oct): the cards show the LIVE load of approved OE Conditions in
  // this scope, so a stored snapshot can never show a stale or survey-blended number. Blend mode
  // keeps the stored snapshot scores.
  const scoringCfg = useMemo(() => normalizeScoring(organization?.scoring), [organization?.scoring]);
  const conditionsOnly = scoringCfg.mode === 'conditions';
  const liveLoad = useMemo(() => {
    if (!conditionsOnly || !organization) return null;
    const headcount = headcountForScope(organization, scope);
    return computeConditionLoad({ conditions: allConditions, scope, headcount, scoring: scoringCfg });
  }, [conditionsOnly, organization, scope, allConditions, scoringCfg]);
  const latestScores = useMemo(() => {
    if (!liveLoad) return storedScores;
    const r = liveLoad.reality;
    const overall = Math.round(((r.reliability + r.efficiency + r.productivity + r.safety) / 4) * 100) / 100;
    return { overall, ...r };
  }, [liveLoad, storedScores]);
  const liveBasis = useMemo(() => {
    if (!liveLoad) return null;
    const n = liveLoad.active + liveLoad.resolvedDecaying;
    const people = liveLoad.people > 0 ? ` · ${liveLoad.people} people` : '';
    return { responseCount: 0, headcount: liveLoad.people, lowConfidence: false, text: n === 0 ? 'no approved OE Conditions yet' : `based on ${n} approved OE ${n === 1 ? 'Condition' : 'Conditions'}${people}`, conditions: n };
  }, [liveLoad]);
  // Adjustments ride on the final (live or stored) score — must come after it is known.
  const { enrichedScores, adjustments: oeAdjustments } = useOEAdjustments(orgId, latestScores);
  const warnings = useMemo(() => (latestScores ? checkThresholds(latestScores, thresholds) : snapshotWarnings || []), [latestScores, thresholds, snapshotWarnings]);
  const warningsByQuadrant = useMemo(() => {
    const map = {};
    warnings.forEach((w) => { if (w.quadrant) map[w.quadrant] = w; });
    return map;
  }, [warnings]);

  const latestSnapshot = snapshots.length > 0 ? snapshots[0] : null;
  const elementScores = latestSnapshot?.elementScores || null;
  const scopeOptions = useMemo(() => scopeOptionsFor(organization, queryScope), [organization, queryScope?.siteId, queryScope?.deptId, queryScope?.nwtId]);

  const dashboardData = useMemo(
    () => ({
      organization,
      scope,
      thresholds,
      snapshots,
      latestSnapshot,
      latestScores,
      enrichedScores,
      elementScores,
      warningsByQuadrant,
      scoringMode: scoringCfg.mode,
      liveBasis,
      activeSurveys,
      surveys,
      responseCounts,
      caseStudies,
      caseStudiesLoading,
      conditions,
      allConditions,
      expandedQuadrant,
      onQuadrantClick,
      preview: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization, scope, thresholds, snapshots, latestScores, enrichedScores, elementScores, warningsByQuadrant, scoringCfg.mode, liveBasis, activeSurveys, surveys, responseCounts, caseStudies, caseStudiesLoading, conditions, allConditions, expandedQuadrant, onQuadrantClick]
  );

  return {
    dashboardData,
    scopeOptions,
    warnings,
    snapshots,
    latestSnapshot,
    latestScores,
    elementScores,
    thresholds,
    totalResponses,
    oeAdjustments,
    enrichedScores,
    loading: scoresLoading || surveysLoading,
  };
}

export default useOEDashboardData;
